// 정비 서랍 표시 모델. 성장·배치·영속 값은 변경하지 않고 기존 명령만 만든다.
import { learnDenial, learnedSkills, SKILL_NODES } from '../shared/growth';
import { heroSkills } from '../shared/skills/registry';
import { jobTierOfSkill } from '../shared/content/jobQuest';
import { SKILL_INFO } from '../shared/rpg';
import { SLOT_COUNT, SLOT_TOTAL, slotsAll, type SlotsView } from '../shared/skillSlots';
import { Scene, type GameCommand, type GrowthView, type PlayableHeroId, type SceneId } from '../shared/types';

// lyra.ts의 현재 타임라인 동작을 한국어로 설명한다. 미구현 content 계획표는 읽지 않는다.
const LYRA_ACTIONS = [
  '전방으로 빠르게 화살 세 발을 연달아 쏜다.',
  '뒤로 물러나며 전방에 부채꼴로 화살 세 발을 쏜다.',
  '잠시 겨눈 뒤 길 위의 적들을 관통하는 화살을 쏜다.',
  '낮게 쏜 화살로 전방의 적을 공중으로 띄운다.',
  '겨눈 적의 자리에 여러 차례 화살비를 내린다.',
  '짧게 뛰어올라 화살을 연사하고 공중 추가타를 넣는다.',
  '전방에 그물 덫을 놓아 밟은 적을 속박한다.',
  '화살이 닿은 곳에 폭발을 일으켜 주변의 적을 공격한다.',
  '회오리로 주변의 적을 끌어당기며 연속 공격한다.',
  '서로 다른 적을 겨냥하는 유도 화살을 발사한다.',
  '공중에서 내려오며 주위에 화살비를 퍼붓는다.',
] as const;

/** 라이라 전직 스킬(12·13) 동작 설명: 갈래(0 윈드 스나이퍼·1 트랩 레인저)마다 다르다(C10-6, lyra.ts 끝) */
const LYRA_JOB_ACTIONS: readonly [readonly [string, string], readonly [string, string]] = [
  ['시위를 끝까지 당겨 길 위의 적을 모두 꿰뚫는 굵은 저격 한 발을 쏜다.', '세 갈래 관통 화살을 다섯 번 퍼붓고 굵은 관통 화살로 마무리한다.'],
  ['전방 일직선에 덫 세 개를 깔아 밟은 적을 속박하고 터뜨린다.', '겨눈 적의 발밑에 속박 덫을 놓고 같은 자리에 연쇄 폭발을 일으킨다.'],
];

/** 전직 스킬(카엘 9·10·라이라 12·13)은 레벨 자동 습득이 아니라 교관 세르의 전직 영수증으로만 열린다 */
function jobUnlockText(tier: 1 | 2): string {
  return tier === 1 ? '전직 I(Lv30) 교관 세르' : '전직 II(Lv40) 교관 세르';
}
/** 카엘 스킬 설명: 1~8은 SKILL_INFO, 전직 스킬 9·10은 갈래마다 달라 공통 안내 문구를 쓴다 */
function kaelDescription(id: number, name: string): string {
  const info = SKILL_INFO.find((x) => x.id === id);
  if (info) return info.desc;
  return (id === 9 ? '전직 I' : '전직 II') + ' 갈래 스킬 「' + name + '」. 교관 세르에게서 전직하면 열린다.';
}

export interface ActiveSkillRow {
  readonly id: number;
  readonly name: string;
  readonly action: string;
  readonly description: string;
  readonly learned: boolean;
  readonly unlockText: string;
  readonly slot: number;
  readonly node: number;
  readonly mp: number;
  readonly cooldown: number;
}

/** job(C10-5·6): 전직 갈래 — 카엘 9·10번(0 블러드 버서커·1 소드 마스터)·라이라 12·13번(0 윈드 스나이퍼·1 트랩 레인저) 이름·설명이 갈래를 따른다 */
export function activeSkillRows(hero: PlayableHeroId, g: Pick<GrowthView, 'level' | 'ranks' | 'kept'> | null, slots: SlotsView, job: 0 | 1 = 0): ActiveSkillRow[] {
  const mask = learnedSkills(g?.level ?? 1, g?.ranks ?? [], hero, g?.kept ?? 0);
  const all = slotsAll(slots);
  return heroSkills(hero, job).map((s, i) => {
    let slot = -1;
    for (let n = 0; n < all.length && n < SLOT_TOTAL; n++) if (all[n] === s.id) { slot = n; break; }
    const jt = jobTierOfSkill(hero, s.id);
    const description = hero === 'kael' ? kaelDescription(s.id, s.name) : jt ? LYRA_JOB_ACTIONS[job === 1 ? 1 : 0][jt - 1] : LYRA_ACTIONS[i] ?? '';
    return {
      id: s.id, name: s.name,
      action: hero === 'kael' ? description.slice(0, description.indexOf('. ') + 1 || description.length) : description,
      description, learned: (mask & (1 << (s.id - 1))) !== 0,
      unlockText: s.unlock.kind === 'free' ? '기본 습득' : jt ? jobUnlockText(jt) : s.unlock.kind === 'level' ? 'Lv.' + s.unlock.level + ' 자동 습득' : '특성에서 습득',
      slot, node: hero === 'kael' ? SKILL_NODES.findIndex((n) => n.kind === 'active' && n.skillId === s.id) : -1,
      mp: s.mpCost, cooldown: s.cooldown,
    };
  });
}

/**
 * 스킬 줄 상태(2026-10-27 「스킬 포인트로 지금 배울/강화할 칸」): learnable = 남은 포인트로 지금 한 랭크 올릴 수 있음(금빛 테두리 + 작은 +),
 * learned = 배움(더 올릴 수 없거나 포인트 없음), locked = 아직 못 씀(자물쇠 + 필요 레벨). 판정은 게임과 같은 learnDenial(트리 노드)을 쓴다.
 */
export type RowUpState = 'learnable' | 'learned' | 'locked';
export function rowUpState(row: Pick<ActiveSkillRow, 'learned' | 'node'>, g: Pick<GrowthView, 'level' | 'ranks' | 'mode'> | null, locked: boolean): RowUpState {
  const canUp = !!g && g.mode === 'ok' && !locked && row.node >= 0 && learnDenial(g.ranks, g.level, row.node) === null;
  return canUp ? 'learnable' : row.learned ? 'learned' : 'locked';
}
/** 잠긴 줄에 붙일 짧은 필요 조건: 'Lv.5'(레벨 자동 습득) 또는 '특성'(트리 노드) */
export function rowLockLabel(row: Pick<ActiveSkillRow, 'unlockText'>): string {
  return row.unlockText.match(/^전직 I+/)?.[0] ?? row.unlockText.match(/Lv\.\d+/)?.[0] ?? '특성';
}

export function skillDrawerAllowed(scene: SceneId, manuallyOpen: boolean, paused: boolean): boolean {
  return scene === Scene.Village && manuallyOpen && !paused;
}

/** 교체·맞바꿈·빈칸 처리는 원본 setSkillSlot 규칙이 판단한다. */
export function slotEditCommand(slot: number, skill: number, rows: readonly ActiveSkillRow[], locked: boolean, extra = 0): GameCommand | null {
  // 환생 추가 칸(12·13)은 열렸을 때만(레인 R)
  if (locked || !Number.isInteger(slot) || slot < 0 || slot >= (extra > 0 ? SLOT_TOTAL : SLOT_COUNT)) return null;
  if (skill !== 0 && !rows.some((s) => s.id === skill && s.learned)) return null;
  return { type: 'setSkillSlot', slot, skill };
}

export class SkillDrawerSelection {
  tab: 'skills' | 'traits' | 'rebirth' = 'skills';
  editing = false;
  slot = 0;
  private readonly selected: Record<PlayableHeroId, number> = { kael: 1, lyra: 1 };
  private readonly expanded: Record<PlayableHeroId, boolean> = { kael: false, lyra: false };
  skill(hero: PlayableHeroId): number { return this.selected[hero]; }
  isExpanded(hero: PlayableHeroId, id: number): boolean { return this.expanded[hero] && this.selected[hero] === id; }
  collapse(): void { this.expanded.kael = this.expanded.lyra = false; }
  toggle(hero: PlayableHeroId, id: number): boolean {
    if (!heroSkills(hero).some((s) => s.id === id)) return false;
    if (this.selected[hero] !== id) return this.select(hero, id);
    this.expanded[hero] = !this.expanded[hero];
    return true;
  }
  select(hero: PlayableHeroId, id: number): boolean {
    if (!heroSkills(hero).some((s) => s.id === id) || (this.selected[hero] === id && this.expanded[hero])) return false;
    this.selected[hero] = id;
    this.expanded[hero] = true;
    return true;
  }
}
